import { Grid, InputAdornment, SelectChangeEvent, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { memo, ReactElement } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { FormTypePracticeVM } from '.';
import ListTypeRegister from 'practice/components/shared/list-type-register';
import ListTypeInitiative from '../shared/list-type-initiative';

interface FormTypePracticeProps {
  disabled?: boolean;
  values?: FormTypePracticeVM;
  isEdit?: boolean;
  required?: boolean;
  onChange: (
    key: keyof FormTypePracticeVM,
    value: FormTypePracticeVM[keyof FormTypePracticeVM]
  ) => void;
  errors: FormikErrors<FormTypePracticeVM> | undefined;
  touched: FormikTouched<FormTypePracticeVM> | undefined;
}

const FormTypePracticeComponent = ({
  disabled,
  values,
  required,
  onChange,
  isEdit,
  touched,
  errors,
}: FormTypePracticeProps): ReactElement => {
  const handleListTypeRegisterChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('typeRegister', event.target.value);
  });

  const handleListTypeInitiativeChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('typeInitiative', event.target.value);
  });

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Codice tipo pratica"
          label="Codice tipo pratica *"
          id="code"
          name="code"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.code || ''}
          onChange={(event) => onChange('code', event.target.value)}
          inputProps={{
            maxLength: '3',
          }}
          disabled={disabled || isEdit}
          error={touched?.code && Boolean(errors?.code)}
          helperText={errors?.code}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          aria-label="Descrizione breve tipo pratica"
          label="Descrizione breve tipo pratica"
          id="descriptionBrief"
          name="descriptionBrief"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.descriptionBrief || ''}
          onChange={(event) => onChange('descriptionBrief', event.target.value)}
          inputProps={{
            maxLength: '50',
          }}
          disabled={disabled}
          error={touched?.descriptionBrief && Boolean(errors?.descriptionBrief)}
          helperText={errors?.descriptionBrief}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <PmiTextField
          aria-label="Descrizione tipo pratica"
          label="Descrizione tipo pratica"
          id="description"
          name="description"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.description || ''}
          onChange={(event) => onChange('description', event.target.value)}
          inputProps={{
            maxLength: '100',
          }}
          disabled={disabled}
          error={touched?.description && Boolean(errors?.description)}
          helperText={errors?.description}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListTypeRegister
          value={values?.typeRegister || ''}
          onChange={handleListTypeRegisterChange}
          error={touched?.typeRegister && Boolean(errors?.typeRegister)}
          disabled={disabled}
          required={required}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListTypeInitiative
          value={values?.typeInitiative || ''}
          onChange={handleListTypeInitiativeChange}
          error={touched?.typeInitiative && Boolean(errors?.typeInitiative)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Unità misura tempo"
          label="Unità misura tempo"
          id="measure"
          name="measure"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.measure || ''}
          onChange={(event) => onChange('measure', event.target.value)}
          inputProps={{
            maxLength: '1',
          }}
          disabled={disabled}
          error={touched?.measure && Boolean(errors?.measure)}
          helperText={errors?.measure}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Nr. tempo standard"
          label="Nr. tempo standard"
          id="timeStandard"
          name="timeStandard"
          fullWidth
          type="number"
          variant="outlined"
          InputProps={{
            endAdornment: <InputAdornment position="end">h</InputAdornment>,
          }}
          value={values?.timeStandard || ''}
          onChange={(event) => onChange('timeStandard', event.target.value)}
          disabled={disabled}
          error={touched?.timeStandard && Boolean(errors?.timeStandard)}
          helperText={errors?.timeStandard}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Nr. tempo max"
          label="Nr. tempo max"
          id="timeMax"
          name="timeMax"
          InputProps={{
            endAdornment: <InputAdornment position="end">h</InputAdornment>,
          }}
          fullWidth
          type="number"
          variant="outlined"
          value={values?.timeMax || ''}
          onChange={(event) => onChange('timeMax', event.target.value)}
          disabled={disabled}
          error={touched?.timeMax && Boolean(errors?.timeMax)}
          helperText={errors?.timeMax}
        />
      </Grid>
    </Grid>
  );
};

FormTypePracticeComponent.displayName = 'Form Type Practice Page component';

const FormTypePractice = memo(FormTypePracticeComponent);

export default FormTypePractice;
